import { Injectable } from '@angular/core';
import { EnvConfig } from '@core/config';
import { convertBlobToBase64, isInternalApi } from '@core/utils';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, switchMap } from 'rxjs/operators';

import { DownloaderService } from '../downloader/downloader.service';

export type ImageObject = string | ArrayBuffer;

export type CachedImage = Observable<ImageObject>;

@Injectable({
  providedIn: 'root',
})
export class ImageCacheService {
  private imageCache: Record<string, CachedImage> = {};

  constructor(private downloader: DownloaderService, private env: EnvConfig) {}

  public getImage(url: string, update: boolean = false, external?: boolean): CachedImage {
    if (!url) {
      return of(null);
    }

    // External/third-party URLs: browser can load them directly.
    if (!isInternalApi(url, this.env.apiUrl)) {
      return of(url);
    }

    if (update) {
      this.clearCacheForUrl(url);
    }

    const cached = this.imageCache[url];
    if (cached) {
      return cached;
    }

    // Cache the in-flight observable immediately so repeated template | async
    // subscriptions (e.g. partners table) share one HTTP request instead of
    // canceling/restarting on every change-detection cycle.
    const request$ = this.downloader.download(url, external).pipe(
      switchMap((blob) => convertBlobToBase64(blob)),
      catchError(() => {
        delete this.imageCache[url];
        return of(null);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.imageCache[url] = request$;
    return request$;
  }

  public resetCache() {
    this.imageCache = {};
  }

  private clearCacheForUrl(url: string): void {
    const baseUrl = url.split('?')[0];
    delete this.imageCache[baseUrl];

    Object.keys(this.imageCache).forEach((key) => {
      if (key.startsWith(baseUrl)) {
        delete this.imageCache[key];
      }
    });
  }
}
